import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Edit2, Save, X } from 'lucide-react';
import { FormField, FormSection, Select, Input, Button } from '../../common/ui';

const ShippingInformation = forwardRef(({ 
  repairData, 
  onSave,
  readOnly = false,
  validationErrors = {},
  externalEditMode
}, ref) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({
    delivery_method: repairData.delivery_method || '',
    delivery_method_other: repairData.delivery_method_other || '',
    tracking_id: repairData.tracking_id || ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState('');

  const effectiveEditing = externalEditMode === 'edit' || isEditing;

  useEffect(() => {
    if (externalEditMode === 'edit' && !isEditing) {
      setEditedData({
        delivery_method: repairData.delivery_method || '',
        delivery_method_other: repairData.delivery_method_other || '',
        tracking_id: repairData.tracking_id || ''
      });
      setIsEditing(true);
    } else if (externalEditMode !== 'edit' && isEditing) {
      setIsEditing(false);
    }
  }, [externalEditMode]);

  useImperativeHandle(ref, () => ({
    getEditedData: () => editedData,
    hasChanges: () => {
      const orig = {
        delivery_method: (repairData.delivery_method || '').trim(),
        delivery_method_other: (repairData.delivery_method_other || '').trim(),
        tracking_id: (repairData.tracking_id || '').trim()
      };
      const curr = {
        delivery_method: (editedData.delivery_method || '').trim(),
        delivery_method_other: (editedData.delivery_method_other || '').trim(),
        tracking_id: (editedData.tracking_id || '').trim()
      };
      return JSON.stringify(orig) !== JSON.stringify(curr);
    },
    validate: () => {
      return { valid: true, error: null };
    },
    reset: () => {
      setEditedData({
        delivery_method: repairData.delivery_method || '',
        delivery_method_other: repairData.delivery_method_other || '',
        tracking_id: repairData.tracking_id || ''
      });
      setValidationError('');
      setIsEditing(false);
    }
  }));

  useEffect(() => {
    if (!effectiveEditing) {
      setEditedData({
        delivery_method: repairData.delivery_method || '',
        delivery_method_other: repairData.delivery_method_other || '',
        tracking_id: repairData.tracking_id || ''
      });
    }
  }, [repairData, effectiveEditing]);

  // Auto-open edit mode when status is ready_for_shipping (standalone only)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!externalEditMode && ['ready_for_shipping', 'waiting_for_payment'].includes(repairData.status) && !readOnly) {
      setIsEditing(true);
    }
  }, [repairData.status, readOnly]);

  const handleEdit = () => {
    setEditedData({
      delivery_method: repairData.delivery_method || '',
      delivery_method_other: repairData.delivery_method_other || '',
      tracking_id: repairData.tracking_id || ''
    });
    setValidationError('');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedData({
      delivery_method: repairData.delivery_method || '',
      delivery_method_other: repairData.delivery_method_other || '',
      tracking_id: repairData.tracking_id || ''
    });
    setValidationError('');
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (onSave) {
      setIsSaving(true);
      setValidationError('');
      try {
        await onSave(editedData);
        setIsEditing(false);
      } catch (error) {
        console.error('Error saving shipping information:', error);
        setValidationError('Failed to save changes');
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedData(prev => ({
      ...prev,
      [name]: value
    }));

    if (validationError) {
      setValidationError('');
    }
  };

  const formatDeliveryMethod = (method) => {
    if (!method) return '';
    switch(method) {
      case 'in_person': return 'In Person';
      case 'dhl': return 'DHL';
      case 'ups': return 'UPS';
      case 'remains_at_naviter': return 'Remains at Naviter';
      case 'other': return 'Other';
      default: return method;
    }
  };

  const hasShippingInfo = repairData.delivery_method || repairData.tracking_id;
  const statusesShowingShipping = ['repaired', 'waiting_for_payment', 'ready_for_shipping', 'completed'];
  const showShippingFields = effectiveEditing || hasShippingInfo || statusesShowingShipping.includes(repairData.status);

  const renderActions = () => {
    if (readOnly || externalEditMode) return null;

    if (isEditing) {
      return (
        <div className="flex gap-2">
          <Button
            onClick={handleSave}
            variant="primary"
            size="xs"
            icon={Save}
            loading={isSaving}
          >
            Save
          </Button>
          <Button
            onClick={handleCancel}
            variant="ghost"
            size="xs"
            icon={X}
            disabled={isSaving}
          >
            Cancel
          </Button>
        </div>
      );
    }

    return (
      <button
        onClick={handleEdit}
        className="text-blue-600 hover:text-blue-700 p-1 rounded hover:bg-blue-50 transition-colors"
        title="Edit shipping information"
      >
        <Edit2 className="h-4 w-4" />
      </button>
    );
  };

  if (!showShippingFields) return null;

  return (
    <FormSection title="Shipping Information" actions={renderActions()}>
      {validationError && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-800">{validationError}</p>
        </div>
      )}

      <div className="space-y-1.5">
        {effectiveEditing ? (
          <div className="bg-gray-50/70 rounded p-2 space-y-1.5">
            <div className="grid grid-cols-1 gap-1.5">
              <FormField 
                size="sm"
                label="Delivery Method"
                error={validationErrors.delivery_method}
              >
                <Select
                  size="sm"
                  name="delivery_method"
                  value={editedData.delivery_method || ''}
                  onChange={handleChange}
                  disabled={isSaving}
                  placeholder="Select method..."
                  error={!!validationErrors.delivery_method}
                >
                  <option value="in_person">In Person</option>
                  <option value="dhl">DHL</option>
                  <option value="ups">UPS</option>
                  <option value="remains_at_naviter">Remains at Naviter</option>
                  <option value="other">Other</option>
                </Select>
              </FormField>

              <FormField 
                size="sm"
                label="Tracking ID"
                error={validationErrors.tracking_id}
              >
                <Input
                  size="sm"
                  type="text"
                  name="tracking_id"
                  value={editedData.tracking_id || ''}
                  onChange={handleChange}
                  placeholder="Tracking number..."
                  disabled={isSaving}
                  error={!!validationErrors.tracking_id}
                />
              </FormField>
            </div>

            {editedData.delivery_method === 'other' && (
              <FormField 
                size="sm"
                label="Other Delivery Method"
                error={validationErrors.delivery_method_other}
              >
                <Input
                  size="sm"
                  type="text"
                  name="delivery_method_other"
                  value={editedData.delivery_method_other || ''}
                  onChange={handleChange}
                  maxLength={40}
                  placeholder="Enter delivery method..."
                  disabled={isSaving}
                  error={!!validationErrors.delivery_method_other}
                />
              </FormField>
            )}
          </div>
        ) : (
          <div className="space-y-1.5">
            <div className="flex items-baseline gap-2">
              <span className="text-xs uppercase tracking-wide font-medium text-gray-500">Delivery</span>
              <span className="text-sm text-gray-900">
                {formatDeliveryMethod(repairData.delivery_method) || <span className="text-gray-400 italic">Not set</span>}
                {repairData.delivery_method === 'other' && repairData.delivery_method_other && (
                  <span className="ml-1">({repairData.delivery_method_other})</span>
                )}
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-xs uppercase tracking-wide font-medium text-gray-500">Tracking</span>
              <span className="text-sm text-gray-900">
                {repairData.tracking_id || <span className="text-gray-400 italic">Not set</span>}
              </span>
            </div>
          </div>
        )}
      </div>
    </FormSection>
  );
});

ShippingInformation.displayName = 'ShippingInformation';

export default ShippingInformation;
